/**
 * WONE-612: 6-box OTP code entry primitive.
 *
 * Single hidden ``<input autocomplete="one-time-code">`` owns focus +
 * the underlying value; six painted boxes show the current digits.
 * iOS Safari + macOS Safari recognise the autocomplete hint and offer
 * Mail-derived autofill above the keyboard. Paste of a 6-digit code
 * works out-of-the-box because input is a real text input.
 *
 * Calls ``onComplete(code)`` exactly once each time the value reaches
 * length 6; rest of the screen wires that to ``verify(login|signup)Otp``.
 *
 * Mobile parity: src/components/inputs/BrandedOtpCodeField.tsx.
 */

import React, { forwardRef, useImperativeHandle, useRef } from "react";
import clsx from "clsx";

import { trackEvent } from "utility/mixpanel";

import styles from "./otp-code-field.module.scss";

export interface OtpCodeFieldHandle {
  focus: () => void;
  /** Clears the value and re-focuses; used on a "didn't match" reset. */
  reset: () => void;
}

export interface OtpCodeFieldProps {
  value: string;
  onChange: (next: string) => void;
  /** Fired when the value reaches length 6 (digits only). */
  onComplete: (code: string) => void;
  hasError?: boolean;
  /** When true, the hidden input is disabled (still receives clicks but no edits). */
  disabled?: boolean;
  /**
   * When true, the boxes are dimmed and a centred spinner overlays the
   * field — used while the initial OTP request is in flight or while a
   * verify call is round-tripping. Implies ``disabled``. Mobile parity:
   * ``BrandedOtpCodeField`` ``loading`` prop (PR #828).
   */
  loading?: boolean;
  autoFocus?: boolean;
  /** ARIA label for the hidden input. */
  ariaLabel?: string;
  className?: string;
  /**
   * WONE-713: when set, emits ``Auth Field Focused`` / ``Auth Field Blurred``
   * Mixpanel events on the hidden input. Field defaults to ``otp``.
   */
  trackingScreen?: string;
}

const LENGTH = 6;
const DIGITS = [0, 1, 2, 3, 4, 5] as const;

function sanitize(input: string): string {
  return input.replace(/\D/g, "").slice(0, LENGTH);
}

export const OtpCodeField = forwardRef<OtpCodeFieldHandle, OtpCodeFieldProps>(
  (
    {
      value,
      onChange,
      onComplete,
      hasError = false,
      disabled = false,
      loading = false,
      autoFocus = false,
      ariaLabel = "One-time code",
      className,
      trackingScreen,
    },
    ref
  ) => {
    const isInactive = disabled || loading;
    const inputRef = useRef<HTMLInputElement>(null);
    const completedRef = useRef<string | null>(null);
    const valueAtFocusRef = useRef<string>("");

    useImperativeHandle(
      ref,
      (): OtpCodeFieldHandle => ({
        focus: () => inputRef.current?.focus(),
        reset: () => {
          completedRef.current = null;
          onChange("");
          requestAnimationFrame(() => inputRef.current?.focus());
        },
      }),
      [onChange]
    );

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
      const next = sanitize(event.target.value);
      onChange(next);
      if (next.length === LENGTH && completedRef.current !== next) {
        completedRef.current = next;
        onComplete(next);
      } else if (next.length < LENGTH) {
        completedRef.current = null;
      }
    };

    return (
      <div
        className={clsx(
          styles["field"],
          loading && styles["field-loading"],
          className
        )}
        onClick={() => !isInactive && inputRef.current?.focus()}
      >
        <input
          ref={inputRef}
          className={styles["hidden-input"]}
          data-testid="auth-otp-input"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          aria-label={ariaLabel}
          value={value}
          onChange={handleChange}
          onFocus={() => {
            if (!trackingScreen) return;
            valueAtFocusRef.current = value;
            trackEvent("Auth Field Focused", {
              Field: "otp",
              Screen: trackingScreen,
            });
          }}
          onBlur={() => {
            if (!trackingScreen) return;
            if (valueAtFocusRef.current === value) return;
            trackEvent("Auth Field Blurred", {
              Field: "otp",
              Screen: trackingScreen,
              "Had Value": value.length > 0,
            });
          }}
          maxLength={LENGTH}
          disabled={isInactive}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
        />
        <div className={styles["boxes"]} aria-hidden>
          {DIGITS.map((i) => {
            const filled = i < value.length;
            const active = !filled && i === value.length;
            return (
              <span
                key={i}
                className={clsx(
                  styles["box"],
                  filled && styles["box-filled"],
                  active && !loading && styles["box-active"],
                  hasError && styles["box-error"]
                )}
              >
                {value[i] ?? ""}
              </span>
            );
          })}
        </div>
        {loading && (
          <div className={styles["spinner-overlay"]} aria-hidden>
            <span
              data-testid="auth-otp-spinner"
              className={clsx("spinner-border", styles["spinner"])}
              role="status"
            />
          </div>
        )}
      </div>
    );
  }
);

OtpCodeField.displayName = "OtpCodeField";
